import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { InvisibleRuleGame } from './InvisibleRuleGame';
export function Hero() {
  const [gameOpen, setGameOpen] = useState(false);
  return <section className="relative min-h-screen flex flex-col justify-center px-6 py-12 md:py-20" data-id="element-44">
      <div className="max-w-7xl mx-auto w-full" data-id="element-45">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-46">
          {/* Text Content */}
          <div className="order-1 lg:order-1" data-id="element-47">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-48">
              <span className="inline-block mb-6 text-[#A88B9D] font-medium tracking-widest uppercase text-sm" data-id="element-49">
                Brand & User Experience Strategist
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif leading-[1.1] mb-8 text-[#4A3B47]" data-id="element-50">
                Helping Startups Build Platforms That Feel Like{' '}
                <span className="italic text-[#A88B9D]" data-id="element-51">Home</span>.
              </h1>
            </motion.div>

            <motion.div className="text-lg md:text-xl leading-relaxed text-[#4A3B47]/80 space-y-6 font-light" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-52">
              <p data-id="element-53">
                If your brand were a hotel, I'd be the one designing the lobby,
                the part that makes people pause, feel something, and want to
                stay longer.
              </p>
              <p data-id="element-54">
                I help tech startups and small businesses build digital
                platforms that feel clear, consistent, and human.
              </p>

              <div className="pt-6 flex flex-wrap gap-4" data-id="element-55">
                <Button href="#services" variant="outline" icon data-id="element-56">
                  My Specialties
                </Button>
                <Button onClick={() => setGameOpen(true)} type="button" data-id="element-57">
                  Play My Game
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Photo */}
          <motion.div className="order-2 lg:order-2 flex flex-col items-center lg:items-end" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-58">
            <div className="relative" data-id="element-59">
              {/* Decorative background shape */}
              <div className="absolute -inset-4 bg-[#A88B9D]/10 rounded-[60px] -rotate-6" data-id="element-60"></div>
              <div className="absolute -inset-4 bg-[#A88B9D]/5 rounded-[60px] rotate-3" data-id="element-61"></div>

              {/* Image container with elegant shape */}
              <div className="relative w-72 h-96 md:w-80 md:h-[28rem] lg:w-96 lg:h-[32rem] overflow-hidden rounded-[40px] shadow-2xl shadow-[#A88B9D]/20" data-id="element-62">
                <img src="https://cdn.magicpatterns.com/uploads/qpxdQ4PiyUi5zkRESnSugA/DK.jpg" alt="Dania - Brand Consultant" className="w-full h-full object-cover object-top" data-id="element-63" />
                {/* Subtle gradient overlay for polish */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#A88B9D]/10 via-transparent to-transparent" data-id="element-64"></div>
              </div>

              {/* Decorative accent */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 border-2 border-[#A88B9D]/30 rounded-full" data-id="element-65"></div>
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-[#A88B9D]/20 rounded-full" data-id="element-66"></div>

              {/* Let's Talk button under photo - aligned to left */}
              <motion.div className="mt-10 flex justify-start" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} data-id="element-67">
                <Button href="#contact" icon data-id="element-68">
                  Let's Talk
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Extended text below on larger screens */}
        <motion.div className="mt-16 lg:mt-24 max-w-3xl" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-69">
          <div className="flex items-start gap-6" data-id="element-70">
            <div className="w-px h-24 bg-[#A88B9D]/30 mt-2 hidden md:block" data-id="element-71"></div>
            <p className="text-lg text-[#4A3B47]/70 font-light leading-relaxed" data-id="element-72">
              The most powerful way to elevate a platform is to build a
              community around it. That's where I come in. I help you create
              loyal communities that turn users into advocates and your brand
              into a movement.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Game overlay */}
      <AnimatePresence data-id="element-73">
        {gameOpen && <motion.div className="fixed inset-0 z-[60] flex items-start md:items-center justify-center p-4 overflow-y-auto" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} data-id="element-74">
            <div className="absolute inset-0 bg-[#1A1814]/70 backdrop-blur-sm" onClick={() => setGameOpen(false)} aria-hidden="true" data-id="element-75" />
            <motion.div className="relative my-8 w-full max-w-[720px]" initial={{
          opacity: 0,
          y: 24,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 24,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-76">
              <InvisibleRuleGame onClose={() => setGameOpen(false)} data-id="element-77" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}